/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecurityHero.module.scss';

const STAT_ICONS = {
  registry: <path d="M4 10h16M6 10v9M10 10v9M14 10v9M18 10v9M5 19h14M12 3l8 5H4z" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.6 2.3 4 5.3 4 8.5s-1.4 6.2-4 8.5c-2.6-2.3-4-5.3-4-8.5s1.4-6.2 4-8.5z" />
    </>
  ),
  layers: (
    <>
      <path d="M12 3.5 21 8l-9 4.5L3 8z" />
      <path d="M3 13l9 4.5 9-4.5" />
      <path d="M3 17.5 12 22l9-4.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2.5" />
    </>
  ),
} as const;

type Translate = (key: string) => string;

const getStats = (t: Translate): { value: number; suffix: string; label: string; icon: keyof typeof STAT_ICONS }[] => [
  { value: 400, suffix: '+', label: t('Government registries, collected directly'), icon: 'registry' },
  { value: 200, suffix: '+', label: t('Countries and jurisdictions covered'), icon: 'globe' },
  { value: 600, suffix: 'M+', label: t('Company profiles'), icon: 'layers' },
  { value: 12, suffix: 'yrs', label: t('Of filing history behind every entity'), icon: 'clock' },
];

export const DefenceSecurityHero = () => {
  const { t } = useInt();
  const stats = getStats(t);
  const statsRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(statsRef, { threshold: 0.2 });
  const [counts, setCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    const steps = 26;
    let step = 0;
    const id = setInterval(() => {
      step += 1;
      const progress = Math.min(step / steps, 1);
      setCounts(stats.map((stat) => Math.round(stat.value * progress)));
      if (progress >= 1) {
        clearInterval(id);
      }
    }, 28);

    return () => clearInterval(id);
  }, [revealed]);

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          size="page"
          title={t('Ground-truth intelligence for global security.')}
          description={t(
            'High-stakes operations demand verified facts, not approximations. Power your security and compliance workflows with corporate data verified directly at the government source.',
          )}
        />
        <div className={styles.actions}>
          <Button type="blue" size="xxl" href="/book-a-demo">
            {t('Book a demo')}
          </Button>
          <Button type="dark-outline" size="xxl" href="/free-trial">
            {t('Screen your supplier base')}
          </Button>
        </div>
      </div>

      <div className={cn(styles.stats, revealed && styles.statsOn)} ref={statsRef}>
        {stats.map((stat, i) => (
          <Link className={styles.stat} route="/data-dictionary" key={stat.label}>
            <span className={styles.statIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {STAT_ICONS[stat.icon]}
              </svg>
            </span>
            <span className={styles.statBody}>
              <span className={styles.statN}>
                {counts[i]}
                <em>{stat.suffix}</em>
              </span>
              <span className={styles.statL}>{stat.label}</span>
            </span>
          </Link>
        ))}
      </div>
    </Section>
  );
};
